/** Builder section 2 — the campaign: its name, the budget (plus other options to price side by side), flight and internal notes. */

import React from 'react';
import { Button } from '../../../ui';
import { fieldId, formatAmount, type PlanDraft } from '../planDraft';
import { MAX_TIERS } from '../vocab';
import { fieldStyle, gridStyle, hintStyle, labelStyle, numberFieldStyle } from '../formStyles';
import FormSection from './FormSection';
import { FieldError, Optional, fieldAria, type ErrorFor } from './fields';

interface Props {
  draft: PlanDraft;
  update: (patch: Partial<PlanDraft>) => void;
  errorFor: ErrorFor;
  disabled?: boolean;
}

const CampaignSection: React.FC<Props> = ({ draft, update, errorFor, disabled = false }) => {
  const budgetError = errorFor('budget');
  const startError = errorFor('flightStart');
  const endError = errorFor('flightEnd');
  const tiersError = errorFor('tiers');

  return (
    <FormSection id="mp-section-campaign" title="Campaign">
      <div>
        <label htmlFor={fieldId('objective')} style={labelStyle}>
          Campaign name <Optional />
        </label>
        <input
          id={fieldId('objective')}
          style={fieldStyle}
          value={draft.objective}
          maxLength={500}
          placeholder="e.g. FY27 AlwaysOn — shown on the plan the client gets"
          disabled={disabled}
          onChange={(e) => update({ objective: e.target.value })}
        />
      </div>

      <div style={gridStyle}>
        <div>
          <label htmlFor={fieldId('budget')} style={labelStyle}>
            Budget (net media, USD)
          </label>
          <input
            id={fieldId('budget')}
            style={numberFieldStyle}
            inputMode="decimal"
            value={draft.budgetUsd}
            placeholder="250,000"
            disabled={disabled}
            onChange={(e) => update({ budgetUsd: e.target.value })}
            onBlur={() => formatAmount(draft.budgetUsd) !== draft.budgetUsd && update({ budgetUsd: formatAmount(draft.budgetUsd) })}
            {...fieldAria('budget', budgetError)}
          />
          <FieldError field="budget" message={budgetError} />
          {draft.tiersUsd.map((t, i) => {
            const field = `tier-${i}`;
            const tierError = errorFor(field);
            return (
              <div key={i} style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    id={fieldId(field)}
                    style={numberFieldStyle}
                    inputMode="decimal"
                    value={t}
                    placeholder="350,000"
                    aria-label={`Budget option ${i + 2}`}
                    disabled={disabled}
                    onChange={(e) => update({ tiersUsd: draft.tiersUsd.map((x, j) => (j === i ? e.target.value : x)) })}
                    onBlur={() => formatAmount(t) !== t && update({ tiersUsd: draft.tiersUsd.map((x, j) => (j === i ? formatAmount(x) : x)) })}
                    {...fieldAria(field, tierError)}
                  />
                  <Button variant="ghost" size="sm" disabled={disabled} onClick={() => update({ tiersUsd: draft.tiersUsd.filter((_, j) => j !== i) })} aria-label={`Remove budget option ${i + 2}`}>
                    ✕
                  </Button>
                </div>
                <FieldError field={field} message={tierError} />
              </div>
            );
          })}
          <FieldError field="tiers" message={tiersError} />
          {draft.tiersUsd.length < MAX_TIERS - 1 && (
            <Button variant="subtle" size="sm" style={{ marginTop: 8 }} disabled={disabled} onClick={() => update({ tiersUsd: [...draft.tiersUsd, ''] })}>
              + Add budget option
            </Button>
          )}
          <div style={hintStyle}>Other budgets are priced side by side — each becomes its own tab in the Sheet.</div>
        </div>
        <div>
          <label htmlFor={fieldId('flightStart')} style={labelStyle}>
            Flight start
          </label>
          <input
            id={fieldId('flightStart')}
            type="date"
            style={fieldStyle}
            value={draft.flightStart}
            disabled={disabled}
            onChange={(e) => update({ flightStart: e.target.value })}
            {...fieldAria('flightStart', startError)}
          />
          <FieldError field="flightStart" message={startError} />
        </div>
        <div>
          <label htmlFor={fieldId('flightEnd')} style={labelStyle}>
            Flight end
          </label>
          <input
            id={fieldId('flightEnd')}
            type="date"
            style={fieldStyle}
            value={draft.flightEnd}
            min={draft.flightStart || undefined}
            disabled={disabled}
            onChange={(e) => update({ flightEnd: e.target.value })}
            {...fieldAria('flightEnd', endError)}
          />
          <FieldError field="flightEnd" message={endError} />
        </div>
      </div>

      <div>
        <label htmlFor={fieldId('notes')} style={labelStyle}>
          Internal notes <Optional>never shown to the client</Optional>
        </label>
        <textarea
          id={fieldId('notes')}
          style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }}
          value={draft.notes}
          maxLength={2000}
          placeholder="Context from the RFP, what the buyer pushed back on last time…"
          disabled={disabled}
          onChange={(e) => update({ notes: e.target.value })}
        />
      </div>
    </FormSection>
  );
};

export default CampaignSection;
